import { Injectable, inject } from '@angular/core';
import { PosSettingsService } from './pos-settings.service';

/**
 * Sound Service
 *
 * Manages sound effects for POS operations
 * Respects the soundEnabled setting from POS configuration
 */
@Injectable({
  providedIn: 'root',
})
export class SoundService {
  private posSettingsService = inject(PosSettingsService);

  // Sound instances (using Web Audio API for better control)
  private audioContext: AudioContext | null = null;

  constructor() {
    // Initialize Audio Context on first user interaction
    if (typeof window !== 'undefined' && 'AudioContext' in window) {
      this.audioContext = new AudioContext();
    }
  }

  /**
   * Play add to cart sound
   * Short, pleasant beep to confirm item was added
   */
  playAddToCart(): void {
    if (!this.isSoundEnabled()) return;

    // Create a simple beep sound (frequency: 800Hz, duration: 100ms)
    this.playBeep(800, 100, 0.3);
  }

  /**
   * Play success sound
   * For successful operations like completing a sale
   */
  playSuccess(): void {
    if (!this.isSoundEnabled()) return;

    // Two-tone success sound
    this.playBeep(600, 80, 0.2);
    setTimeout(() => this.playBeep(800, 120, 0.25), 100);
  }

  /**
   * Play error sound
   * For errors or invalid operations
   */
  playError(): void {
    if (!this.isSoundEnabled()) return;

    // Lower frequency, longer duration for error
    this.playBeep(200, 200, 0.3);
  }

  /**
   * Play scan sound
   * For barcode scanning
   */
  playScan(): void {
    if (!this.isSoundEnabled()) return;

    // Quick, high-pitched beep
    this.playBeep(1200, 50, 0.2);
  }

  /**
   * Check if sounds are enabled in POS settings
   */
  private isSoundEnabled(): boolean {
    return this.posSettingsService.posSettings()?.pos.soundEnabled ?? true;
  }

  /**
   * Play a beep sound with specified frequency and duration
   *
   * @param frequency - Frequency in Hz (e.g., 800)
   * @param duration - Duration in milliseconds (e.g., 100)
   * @param volume - Volume level 0.0 to 1.0 (e.g., 0.3)
   */
  private playBeep(frequency: number, duration: number, volume: number = 0.3): void {
    if (!this.audioContext) {
      // Fallback to simple beep if AudioContext not available
      this.playSimpleBeep();
      return;
    }

    try {
      // Resume audio context if suspended (required by some browsers)
      if (this.audioContext.state === 'suspended') {
        this.audioContext.resume();
      }

      // Create oscillator for tone
      const oscillator = this.audioContext.createOscillator();
      const gainNode = this.audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(this.audioContext.destination);

      // Configure sound
      oscillator.frequency.value = frequency;
      oscillator.type = 'sine'; // Smooth sine wave

      // Configure volume envelope (fade in/out)
      const now = this.audioContext.currentTime;
      gainNode.gain.setValueAtTime(0, now);
      gainNode.gain.linearRampToValueAtTime(volume, now + 0.01); // Quick fade in
      gainNode.gain.linearRampToValueAtTime(0, now + duration / 1000); // Fade out

      // Play
      oscillator.start(now);
      oscillator.stop(now + duration / 1000);
    } catch (error) {
      console.warn('Error playing beep sound:', error);
    }
  }

  /**
   * Fallback simple beep using system bell (very basic)
   */
  private playSimpleBeep(): void {
    // Create a very short audio element as fallback
    const audio = new Audio(
      'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBTGH0fPTgjMGHm7A7+OZTA0PVqzn77BdGAk+ltryxnMpBSuBzvLZiTYIGWi77eafTRAKUKfj8LZkGwc4ktfxy3ksAyR3x/DfkD8LFV6z6eqoVRQLRp/h8r9sIQQ='
    );
    audio.volume = 0.3;
    audio.play().catch(() => {
      // Silently fail if audio can't play
    });
  }
}
